import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  BsFileEarmarkPdfFill,
  BsDownload,
  BsShieldLockFill,
  BsTelephoneFill,
  BsEnvelopeFill,
  BsFillBookmarkCheckFill,
  BsGrid3X3GapFill,
} from "react-icons/bs";

const ResourceDownload = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title =
      "B2B Digital Asset Vault & Institutional Materials | NGO Waters";
  }, []);

  const documentRegistry = [
    { name: "Executive Enterprise USP", file: "/resources/company-usp.pdf" },
    {
      name: "Industrial Product Brochure",
      file: "/resources/product-brochure.pdf",
    },
    {
      name: "Commercial Sales Protocol Guide",
      file: "/resources/sales-training.pdf",
    },
    {
      name: "Corporate Compliance Policies",
      file: "/resources/company-policies.pdf",
    },
    {
      name: "Wholesale Trade Pricing Matrix",
      file: "/resources/pricing-list.pdf",
    },
    {
      name: "Strategic Pipeline Campaigns",
      file: "/resources/Future Campaigns.pdf",
    },
    {
      name: "Distributor Franchise Agreement",
      file: "/resources/distributor-guidelines.pdf",
    },
  ];

  const fluidSpring = { type: "spring", stiffness: 45, damping: 15 };
  const microSpring = { type: "spring", stiffness: 300, damping: 20 };

  return (
    <motion.div
      className="container-fluid min-vh-100 position-relative overflow-hidden py-5 px-3 px-md-5 bg-light"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Low-Opacity Subtle Ambient Ambient Blur Layers */}
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "550px",
          height: "550px",
          background: "rgba(54, 191, 216, 0.03)",
          top: "-180px",
          right: "-40px",
          filter: "blur(130px)",
          pointerEvents: "none",
        }}
      />
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "450px",
          height: "450px",
          background: "rgba(166, 123, 91, 0.02)",
          bottom: "20%",
          left: "-60px",
          filter: "blur(100px)",
          pointerEvents: "none",
        }}
      />

      {/* Production-Grade Technical SEO / GEO / AEO Structured Microdata Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemPage",
          name: "NGO Waters Corporate Institutional Resources Vault",
          description:
            "Secure access terminal for certified operational metrics, B2B price lists, and logistical distribution guidelines for NGO Waters.",
          provider: {
            "@type": "Organization",
            name: "PONIA FOOD AND BEVERAGE PRIVATE LIMITED",
            duns: "CIN: U11043UP2024PTC213517",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Village - Subakara, Thana - Gonda",
              addressLocality: "Aligarh",
              addressRegion: "Uttar Pradesh",
              postalCode: "202123",
              addressCountry: "IN",
            },
          },
        })}
      </script>

      {/* ================= VAULT HERO TITLE HEADER ================= */}
      <section className="container py-4 position-relative z-1 mb-5 text-center">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span
            className="badge px-3 py-2 mb-3 text-uppercase fw-semibold tracking-wider"
            style={{
              backgroundColor: "rgba(26, 60, 107, 0.06)",
              color: "var(--brand-deep-blue)",
              fontSize: "0.72rem",
              borderRadius: "6px",
            }}
          >
            Secure Corporate Asset Registry
          </span>
          <h1
            className="display-5 fw-bold mb-3"
            style={{ color: "var(--brand-deep-blue)", letterSpacing: "-1.5px" }}
          >
            Institutional Material Hub
          </h1>
          <p
            className="text-secondary small mx-auto mb-0"
            style={{ maxWidth: "580px", lineHeight: "1.65" }}
          >
            Direct cryptographic downloading access terminal for technical
            verification data sheets, supply pipeline blueprints, and legal
            franchise compliance documentation.
          </p>
        </motion.div>
      </section>

      {/* ================= DIGITAL REGISTRY CARDS GRID ================= */}
      <section className="container mb-5 position-relative z-1">
        <motion.div
          className="row g-4 justify-content-center"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-40px" }}
          variants={{
            hidden: { opacity: 0 },
            show: { opacity: 1, transition: { staggerChildren: 0.1 } },
          }}
        >
          {documentRegistry.map((resource, index) => (
            <div key={index} className="col-sm-6 col-md-4 col-xl-3">
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 15 },
                  show: { opacity: 1, y: 0, transition: fluidSpring },
                }}
                whileHover={{
                  y: -6,
                  boxShadow: "0px 12px 30px rgba(26, 60, 107, 0.03)",
                }}
                className="card border-0 h-100 bg-white p-4 justify-content-between text-center"
                style={{
                  borderRadius: "24px",
                  border: "1px solid rgba(0,0,0,0.03)",
                  transition: "transform 0.25s ease",
                }}
              >
                <div className="d-flex flex-column align-items-center">
                  <div
                    className="rounded-3 d-flex align-items-center justify-content-center mb-3"
                    style={{
                      backgroundColor: "rgba(220, 53, 69, 0.05)",
                      width: "42px",
                      height: "42px",
                    }}
                  >
                    <BsFileEarmarkPdfFill className="fs-5 text-danger" />
                  </div>
                  <h5
                    className="fw-bold text-dark mb-0 px-2"
                    style={{ fontSize: "0.95rem", lineHeight: "1.4" }}
                  >
                    {resource.name}
                  </h5>
                </div>

                <div className="pt-4">
                  <motion.a
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    transition={microSpring}
                    href={resource.file}
                    download
                    className="btn btn-outline-dark w-100 py-2 d-flex align-items-center justify-content-center gap-2 fw-semibold"
                    style={{ borderRadius: "10px", fontSize: "0.82rem" }}
                  >
                    <BsDownload /> Download PDF
                  </motion.a>
                </div>
              </motion.div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* ================= ABOUT REGIONAL OPERATIONS FRAMEWORK ================= */}
      <section className="container my-5 position-relative z-1">
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-9">
            <motion.div
              className="p-4 p-md-5 bg-white border"
              style={{
                borderRadius: "24px",
                borderColor: "rgba(0,0,0,0.03)",
                boxShadow: "0 8px 30px rgba(0,0,0,0.01)",
              }}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="d-flex align-items-center gap-2 mb-3">
                <BsGrid3X3GapFill
                  style={{ color: "var(--brand-mineral-aqua)" }}
                />
                <h4
                  className="fw-bold mb-0 fs-5"
                  style={{ color: "var(--brand-deep-blue)" }}
                >
                  Operational Ecosystem Context
                </h4>
              </div>
              <p
                className="text-secondary small mb-0"
                style={{ lineHeight: "1.75" }}
              >
                NGO Waters systematic logistics lines operate under precision
                quality validation protocols to dispatch crisp, premium
                trace-mineral solutions across critical regional wholesale
                channels. Managed strategically under the corporate supervision
                of <strong>PONIA FOOD AND BEVERAGE PRIVATE LIMITED</strong>, our
                automated bottling infrastructure channels 50% of clean net
                revenues cleanly toward funding rural fresh water boring systems
                and civic sanitation networks. The technical resources archived
                above enable partner nodes to remain fully aligned with our
                deployment standards.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================= STRUCTURAL IMPORTANCE GRID ================= */}
      <section className="container my-5 position-relative z-1">
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-9">
            <div
              className="p-4 p-md-5 bg-white border"
              style={{ borderRadius: "24px", borderColor: "rgba(0,0,0,0.03)" }}
            >
              <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom border-light">
                <BsShieldLockFill className="text-muted fs-5" />
                <h4
                  className="fw-bold mb-0 fs-5"
                  style={{ color: "var(--brand-deep-blue)" }}
                >
                  Protocol Verification Parameters
                </h4>
              </div>

              <div className="row g-4">
                <div className="col-md-6">
                  <div
                    className="p-3 rounded-3 h-100"
                    style={{
                      backgroundColor: "rgba(0,0,0,0.005)",
                      border: "1px solid rgba(0,0,0,0.015)",
                    }}
                  >
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <BsFillBookmarkCheckFill className="text-success small" />
                      <h6
                        className="fw-bold text-dark mb-0"
                        style={{ fontSize: "0.9rem" }}
                      >
                        Enterprise USP Layouts
                      </h6>
                    </div>
                    <p
                      className="text-muted small mb-0"
                      style={{ lineHeight: "1.5" }}
                    >
                      Establishes structural reference configurations enabling
                      logistics representatives to map our micro-mineral
                      filtration metrics cleanly during high-volume contract
                      reviews.
                    </p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div
                    className="p-3 rounded-3 h-100"
                    style={{
                      backgroundColor: "rgba(0,0,0,0.005)",
                      border: "1px solid rgba(0,0,0,0.015)",
                    }}
                  >
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <BsFillBookmarkCheckFill className="text-success small" />
                      <h6
                        className="fw-bold text-dark mb-0"
                        style={{ fontSize: "0.9rem" }}
                      >
                        Commercial Sales Blueprints
                      </h6>
                    </div>
                    <p
                      className="text-muted small mb-0"
                      style={{ lineHeight: "1.5" }}
                    >
                      Standardizes structural operational parameters to cleanly
                      accelerate supply pipeline onboarding across multi-level
                      regional distribution grids.
                    </p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div
                    className="p-3 rounded-3 h-100"
                    style={{
                      backgroundColor: "rgba(0,0,0,0.005)",
                      border: "1px solid rgba(0,0,0,0.015)",
                    }}
                  >
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <BsFillBookmarkCheckFill className="text-success small" />
                      <h6
                        className="fw-bold text-dark mb-0"
                        style={{ fontSize: "0.9rem" }}
                      >
                        Distributor Logistics Agreement
                      </h6>
                    </div>
                    <p
                      className="text-muted small mb-0"
                      style={{ lineHeight: "1.5" }}
                    >
                      Enforces strict food-safety guidelines, retail pricing
                      caps, and regional supply boundary parameters to firmly
                      defend ecosystem integrity.
                    </p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div
                    className="p-3 rounded-3 h-100"
                    style={{
                      backgroundColor: "rgba(0,0,0,0.005)",
                      border: "1px solid rgba(0,0,0,0.015)",
                    }}
                  >
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <BsFillBookmarkCheckFill className="text-success small" />
                      <h6
                        className="fw-bold text-dark mb-0"
                        style={{ fontSize: "0.9rem" }}
                      >
                        Wholesale Pricing Framework
                      </h6>
                    </div>
                    <p
                      className="text-muted small mb-0"
                      style={{ lineHeight: "1.5" }}
                    >
                      Maintains legal cost transparency matrix controls across
                      institutional procurement contracts to completely
                      neutralize mid-channel price exploitation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= HIGH-END ASSISTANCE PANEL ================= */}
      <section className="container my-5 position-relative z-1">
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-9">
            <div
              className="bg-white border p-4 p-md-5 text-center text-md-start"
              style={{
                borderRadius: "24px",
                borderColor: "rgba(0,0,0,0.04)",
                borderLeft: "5px solid var(--brand-copper)",
              }}
            >
              <div className="row align-items-center g-4">
                <div className="col-md-7">
                  <h4
                    className="fw-bold mb-2 fs-5"
                    style={{ color: "var(--brand-deep-blue)" }}
                  >
                    Require Specific Regulatory Certification?
                  </h4>
                  <p
                    className="text-muted small mb-0"
                    style={{ lineHeight: "1.6" }}
                  >
                    For custom custom chemical laboratory audits, regional BIS
                    compliance data packets, or customized state logistics
                    clearances, directly trigger a request to our administrative
                    division.
                  </p>
                </div>
                <div className="col-md-5 text-md-end">
                  <div className="d-flex flex-wrap justify-content-center justify-content-md-end gap-2">
                    <a
                      href="tel:+917078430767"
                      className="btn btn-sm btn-outline-dark px-3 py-2 fw-bold d-inline-flex align-items-center gap-2"
                      style={{ borderRadius: "8px", fontSize: "0.8rem" }}
                    >
                      <BsTelephoneFill /> +91 7078430767
                    </a>
                    <a
                      href="mailto:support@ngowaters.com"
                      className="btn btn-sm text-white px-3 py-2 fw-bold border-0 d-inline-flex align-items-center gap-2"
                      style={{
                        backgroundColor: "var(--brand-deep-blue)",
                        borderRadius: "8px",
                        fontSize: "0.8rem",
                      }}
                    >
                      <BsEnvelopeFill /> support@ngowaters.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default ResourceDownload;
